import type { BufferGeometry } from 'three';
import { hexToLinear, scaleColor } from '../../core/color';
import type { PaletteModel } from '../../data/types';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { rounded, box, horseLeg } from './lifeGeometry';

/** An animal of the cast: its body, a fore and a hind leg, and how they hang and swing. */
export interface LifeModel {
  readonly body: BufferGeometry;
  readonly arm: BufferGeometry;
  readonly leg: BufferGeometry;
  readonly shoulderY: number;
  readonly shoulderHalf: number;
  readonly hipY: number;
  readonly hipHalf: number;
  readonly foreX: number;
  readonly hindX: number;
  readonly hindPhase: number;
  readonly strideM: number;
  readonly stepLiftM: number;
  readonly sole: readonly [number, number, number];
}

const DARK = hexToLinear('#3a3225');

/** A small, unarmoured working horse. Proportions and coat are modelling estimates. */
export function buildHorseModel(palette: PaletteModel): LifeModel {
  const b = createMeshBuilder(),
    a = createMeshBuilder(),
    l = createMeshBuilder();
  const coat = hexToLinear('#89704f'),
    hair = palette.shipHull.p10.linear;
  // Barrel, shoulder and rump share a continuous closed eight-sided body.
  rounded(b, coat, [
    { y: 0.98, x: 0, depth: 0.77, half: 0.25 },
    { y: 1.21, x: 0, depth: 0.92, half: 0.34 },
    { y: 1.52, x: -0.02, depth: 0.76, half: 0.28 },
  ]);
  rounded(b, coat, [
    { y: 0.965, x: 0.6, depth: 0.175, half: 0.285 },
    { y: 1.15, x: 0.67, depth: 0.26, half: 0.3 },
    { y: 1.38, x: 0.75, depth: 0.28, half: 0.25 },
    { y: 1.66, x: 0.89, depth: 0.225, half: 0.17 },
    { y: 2.025, x: 1.0, depth: 0.145, half: 0.112 },
  ]);
  rounded(b, coat, [
    { y: 1.71, x: 1.365, depth: 0.14, half: 0.078 },
    { y: 1.82, x: 1.32, depth: 0.185, half: 0.106 },
    { y: 1.966, x: 1.17, depth: 0.245, half: 0.143 },
    { y: 2.095, x: 1.055, depth: 0.16, half: 0.112 },
  ]);
  // The soft muzzle carries a quieter tone than the face, with small dark nostril marks.
  rounded(b, scaleColor(coat, 0.8), [
    { y: 1.708, x: 1.392, depth: 0.123, half: 0.075 },
    { y: 1.738, x: 1.407, depth: 0.13, half: 0.091 },
    { y: 1.802, x: 1.397, depth: 0.141, half: 0.098 },
    { y: 1.824, x: 1.37, depth: 0.145, half: 0.091 },
  ]);
  for (const z of [-0.056, 0.056])
    rounded(
      b,
      DARK,
      [
        { y: 1.774, x: 1.528, z, depth: 0.018, half: 0.013 },
        { y: 1.794, x: 1.526, z, depth: 0.021, half: 0.017 },
        { y: 1.808, x: 1.518, z, depth: 0.012, half: 0.009 },
      ],
      4
    );
  box(b, scaleColor(coat, 0.5), [1.515, 1.715, -0.045], [1.537, 1.724, 0.045]);
  for (const z of [-0.1, 0.1]) {
    rounded(
      b,
      scaleColor(coat, 0.9),
      [
        { y: 2.078, x: 1.0, z: z * 0.85, depth: 0.046, half: 0.038 },
        { y: 2.163, x: 1.015, z: z * 1.05, depth: 0.033, half: 0.026 },
        { y: 2.22, x: 1.035, z: z * 1.1, depth: 0.008, half: 0.008 },
      ],
      4
    );
    box(b, DARK, [1.185, 1.942, z < 0 ? -0.146 : 0.134], [1.225, 1.965, z < 0 ? -0.134 : 0.146]);
  }
  rounded(
    b,
    hair,
    [
      { y: 2.008, x: 1.214, depth: 0.024, half: 0.025 },
      { y: 2.085, x: 1.165, depth: 0.06, half: 0.06 },
      { y: 2.106, x: 1.085, depth: 0.07, half: 0.079 },
    ],
    6
  );
  // Dark mane follows the sloping neck, rather than a giant geometric fan.
  for (let i = 0; i < 6; i++) {
    const y = 1.43 + i * 0.095,
      x = 0.48 + i * 0.048;
    box(b, hair, [x - 0.04, y, -0.075], [x + 0.045, y + 0.13, 0.075]);
  }
  rounded(b, hair, [
    { y: 0.36, x: -1.01, depth: 0.055, half: 0.07 },
    { y: 0.9, x: -0.97, depth: 0.075, half: 0.07 },
    { y: 1.32, x: -0.86, depth: 0.05, half: 0.06 },
  ]);
  // Modest leather bridle; no modern saddle, rider, cart or caparison added.
  rounded(
    b,
    hair,
    [
      { y: 1.826, x: 1.331, depth: 0.187, half: 0.11 },
      { y: 1.842, x: 1.315, depth: 0.194, half: 0.115 },
    ],
    6
  );
  horseLeg(a, coat, hair, false);
  horseLeg(l, coat, hair, true);
  return {
    body: b.finish(),
    arm: a.finish(),
    leg: l.finish(),
    shoulderY: 1.03,
    shoulderHalf: 0.23,
    hipY: 1.03,
    hipHalf: 0.24,
    foreX: 0.63,
    hindX: -0.65,
    hindPhase: 0.25,
    strideM: 1.1,
    stepLiftM: 0.045,
    sole: [-0.06, 0.13, -1.03],
  };
}
